import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import './Layout.css';
import { useMsal } from '@azure/msal-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { clearStoredEmailToken } from '@/auth/emailAuth';
import { useUser } from '@/context/UserContext';
import { getNavItems, ROLE_LABELS, canAccessPeerReviews, canAccessSupport } from '@/auth/roles';
import { apiGet } from '@/api/client';
import LinkExistingAccount from '@/components/LinkExistingAccount';
import logoUrl from '@/assets/silisense-logo.png';

const SupportChatWidget = lazy(() => import('@/pages/Support'));

export default function Layout() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { isAuthenticated, authMethod } = useAuth();
  const { user, role, loading, notProvisioned, refetch } = useUser();
  const name = user?.name ?? user?.email ?? accounts[0]?.name ?? accounts[0]?.username ?? 'User';
  const navItems = getNavItems(role);
  const [peerPending, setPeerPending] = useState(0);

  useEffect(() => {
    if (!isAuthenticated || notProvisioned || !canAccessPeerReviews(role)) {
      setPeerPending(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      apiGet<{ pending: number }>('/v1/peer-reviews/pending-count')
        .then((r) => {
          if (!cancelled) setPeerPending(Number(r.pending) || 0);
        })
        .catch(() => {
          if (!cancelled) setPeerPending(0);
        });
    };
    load();
    const t = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [isAuthenticated, notProvisioned, role]);

  const handleLogout = () => {
    if (authMethod === 'email') {
      clearStoredEmailToken();
      navigate('/login', { replace: true });
      return;
    }
    instance.logoutRedirect().catch((e) => console.error(e));
  };

  if (notProvisioned) {
    return (
      <div className="layout" style={{ padding: '2rem', maxWidth: '32rem', margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '1rem' }}>Account not provisioned</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
          Microsoft signed you in, but this address is not a SILISENSE account yet. Link your existing email and password, or ask an administrator to provision access.
        </p>
        <LinkExistingAccount onLinked={refetch} />
        <button type="button" onClick={handleLogout} className="btn-ghost" style={{ marginTop: '1rem' }}>Sign out</button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Not signed in.</p>
        <button type="button" onClick={() => navigate('/login')} className="btn-primary">Sign in</button>
      </div>
    );
  }

  return (
    <div className="layout">
      <header className="layout-header">
        <NavLink to="/" className="layout-brand" title="SILISENSE home">
          <img
            src={logoUrl}
            alt="SILISENSE — Academic Integrity Reimagined"
            className="brand-logo"
            width={280}
            height={72}
            decoding="async"
          />
        </NavLink>
        <nav className="layout-nav">
          {navItems.map(({ to, label }) => (
            <NavLink key={to} to={to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              {label}
              {to === '/peer-reviews' && peerPending > 0 ? (
                <span
                  className="nav-badge"
                  style={{
                    marginLeft: 6,
                    display: 'inline-block',
                    minWidth: 18,
                    padding: '0 5px',
                    borderRadius: 9,
                    fontSize: 11,
                    lineHeight: '18px',
                    textAlign: 'center',
                    background: 'var(--accent, #38bdf8)',
                    color: '#0f172a',
                    fontWeight: 700,
                  }}
                >
                  {peerPending > 99 ? '99+' : peerPending}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="layout-user">
          {!loading && <span className="user-role">{ROLE_LABELS[role]}</span>}
          <span className="user-name">{name}</span>
          <button type="button" onClick={handleLogout} className="btn-ghost">Sign out</button>
        </div>
      </header>
      <main className="layout-main">
        <Outlet />
      </main>
      {canAccessSupport(role) ? (
        <Suspense fallback={null}>
          <SupportChatWidget />
        </Suspense>
      ) : null}
    </div>
  );
}
